import { useMemo, useState } from 'react'

import moment from 'moment'
import { useLocation } from 'react-router'

import { formatYYYYMMDD } from '@webapp/core/utils/dates/formatYYYYMMDD/formatYYYYMMDD'
import { type ReportConfig } from '@webapp/modules/reports/config/ReportConfig'
import {
  expenseReportConfig,
  secondaryCategoryReportConfig,
  userReportConfig,
  vendorReportConfig,
} from '@webapp/modules/reports/config/reportConfigs'
import {
  type ExportTaskType,
  ExportTaskTypes,
} from '@webapp/modules/reports/entities/enums/ExportTaskTypeEnum'
import { type ReportExportTask } from '@webapp/modules/reports/entities/ReportExportTask'
import { useDeleteExportTaskPresenter } from '@webapp/modules/reports/features/delete-export-task/presenters/UseDeleteExportTaskPresenter'

const REPORT_CONFIGS: ReportConfig[] = [
  vendorReportConfig,
  userReportConfig,
  secondaryCategoryReportConfig,
  expenseReportConfig,
]

export interface ExportHistoryReferrer {
  /** The originating report, or null when the referrer is absent/unknown */
  reportConfig: ReportConfig | null
  /** Detail-page id when the referrer came from a details view (BHV-008) */
  entityId: number | null
  entityName: string | null
}

/**
 * Parses the `referrer` query param (`report.type[.id[.name]]` — Angular
 * typeStr parity) into the originating report config plus the optional
 * detail id/name (BHV-008). Shared with `ReportExportHistoryPage`, which
 * owns the breadcrumb chrome (`<Page breadcrumbs>` is props-only — see
 * arch-memory "Dynamic breadcrumb entity names resolve at Page level").
 */
export const parseExportHistoryReferrer = (
  search: string
): ExportHistoryReferrer => {
  const referrer = new URLSearchParams(search).get('referrer')

  if (!referrer) {
    return { reportConfig: null, entityId: null, entityName: null }
  }

  const parts = referrer.split('.')
  const referrerType = parts.slice(0, 2).join('.')

  const reportConfig =
    REPORT_CONFIGS.find((config) => config.referrerType === referrerType) ??
    null

  const parsedId = parts[2] ? Number(parts[2]) : NaN
  const entityId = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null
  // Entity names may themselves contain dots — rejoin the remainder.
  const entityName = entityId ? parts.slice(3).join('.') || null : null

  return { reportConfig, entityId, entityName }
}

// Orchestration only — this companion hook calls presenter hooks, never
// Model hooks from features/*/models/ (Feature Set Companion Hook Layer
// Boundary).
export const useReportExportHistoryView = () => {
  const location = useLocation()

  const referrer = useMemo(
    () => parseExportHistoryReferrer(location.search),
    [location.search]
  )

  // Every referrer report (orders + expenses) filters on the same task
  // type codes (BHV-035).
  const typeFilter: ExportTaskType[] = [
    ExportTaskTypes.ORDER_ITEMS,
    ExportTaskTypes.EXPENSE_ITEMS,
  ]

  // Default date window: last 3 months → tomorrow (BHV-035).
  const [dateRange, setDateRange] = useState(() => ({
    from: formatYYYYMMDD(moment().subtract(3, 'months').toDate()),
    to: formatYYYYMMDD(moment().add(1, 'day').toDate()),
  }))

  const [pendingDeleteTask, setPendingDeleteTask] =
    useState<ReportExportTask | null>(null)

  const { commands } = useDeleteExportTaskPresenter()

  const handleDateRangeChange = ({
    startDate,
    endDate,
  }: {
    startDate: Date | null
    endDate: Date | null
  }) => {
    if (!startDate || !endDate) {
      return
    }

    setDateRange({
      from: formatYYYYMMDD(startDate),
      to: formatYYYYMMDD(endDate),
    })
  }

  const handleDeleteRequest = (task: ReportExportTask) => {
    setPendingDeleteTask(task)
  }

  const handleDismissDelete = () => {
    setPendingDeleteTask(null)
  }

  // The feature owns success/error toasts and never rejects; the row
  // disappears via the feature's list-key invalidation (BHV-037). Call
  // `done()` BEFORE clearing the state that renders the modal
  // (arch-memory ConfirmationModal gotcha).
  const handleConfirmDelete = async (done: () => void) => {
    if (pendingDeleteTask) {
      await commands.deleteExportTask(pendingDeleteTask.uuid)
    }

    done()
    setPendingDeleteTask(null)
  }

  return {
    data: {
      referrer,
      typeFilter,
      dateRange,
    },
    states: {
      pendingDeleteTask,
    },
    methods: {
      handleDateRangeChange,
      handleDeleteRequest,
      handleDismissDelete,
      handleConfirmDelete,
    },
  }
}
